import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { FLAGSHIPS, type Flagship } from "@/lib/brand";
import PhoneMockup from "@/components/public/mockups/PhoneMockup";

const FLAGSHIP_HUE: Record<string, number> = {
    kasioon: 280,
    "carwhats-group": 220,
    carwhats: 160,
};

const BADGE: Record<string, string> = {
    kasioon: "منصة شاملة",
    "carwhats-group": "بوابة B2B",
    carwhats: "سوق سيارات ذكي",
};

const FlagshipCard = ({
    flagship,
    num,
    tilt,
}: {
    flagship: Flagship;
    num: string;
    tilt: number;
}) => {
    const hue = FLAGSHIP_HUE[flagship.key] ?? 260;
    const accent = `hsl(${hue} 90% 60%)`;
    const accentSoft = `hsl(${hue} 90% 60% / 0.15)`;
    const accentGlow = `hsl(${hue} 90% 60% / 0.3)`;
    const badge = BADGE[flagship.key] ?? "FLAGSHIP";
    const imageSrc = `/mockups/${flagship.key}-phone.png`;

    return (
        <div
            className="glass-v2 hover-lift-v2 rounded-[32px] p-7 md:p-8 group relative overflow-hidden"
            data-cursor="hover"
        >
            {/* Flagship-tinted ambient glow */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: `radial-gradient(500px circle at 50% 0%, ${accentGlow}, transparent 65%)`,
                    opacity: 0.6,
                }}
            />

            {/* Top accent line */}
            <div
                aria-hidden
                className="absolute top-0 inset-x-8 h-px opacity-50 pointer-events-none"
                style={{
                    background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                }}
            />

            <div className="relative">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <span className="font-v2-label text-[10px] text-white/50">
                        FLAGSHIP · {badge}
                    </span>
                    <span className="font-v2-label text-[10px] text-white/40">
                        {num} / 03
                    </span>
                </div>

                {/* Phone mockup + name pair */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-6 text-center sm:text-start">
                    <div
                        className="shrink-0 relative"
                        style={{ perspective: "1200px" }}
                    >
                        <div
                            className="animate-bob-v2"
                            style={{
                                transform: `rotateY(${tilt}deg) rotateX(6deg) rotateZ(${tilt / 6}deg)`,
                                filter: "drop-shadow(0 25px 40px rgba(0,0,0,0.6))",
                                transformStyle: "preserve-3d",
                            }}
                        >
                            <PhoneMockup size="sm" imageSrc={imageSrc} />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0 sm:pt-6">
                        <h3 className="font-v2-display text-2xl md:text-3xl leading-tight mb-1">
                            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                                {flagship.name}
                            </span>
                        </h3>
                        <p className="font-v2-display text-base md:text-lg text-white/80 mb-3">
                            {flagship.nameAr}
                        </p>
                    </div>
                </div>

                {/* Description */}
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-medium mb-5">
                    {flagship.descArLong}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {flagship.tags.map((t) => (
                        <span
                            key={t}
                            className="font-v2-label text-[10px] px-3 py-1 rounded-full border"
                            style={{
                                background: accentSoft,
                                borderColor: `hsl(${hue} 90% 60% / 0.3)`,
                                color: `hsl(${hue} 90% 85%)`,
                            }}
                        >
                            {t}
                        </span>
                    ))}
                </div>

                {/* CTA */}
                <a
                    href={flagship.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                    data-cursor="hover"
                >
                    زيارة الموقع
                    <ExternalLink className="w-3.5 h-3.5" />
                </a>
            </div>
        </div>
    );
};

const FlagshipRow = ({
    flagship,
    index,
}: {
    flagship: Flagship;
    index: number;
}) => {
    const isEven = index % 2 === 0;
    const num = (index + 1).toString().padStart(2, "0");
    const tilt = isEven ? -18 : 18; // phone tilts toward the spine

    return (
        <motion.li
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.9,
                ease: [0.23, 1, 0.32, 1],
                delay: index * 0.05,
            }}
            className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-16 items-center"
        >
            {/* Center node on spine */}
            <div
                aria-hidden
                className="hidden md:flex absolute right-1/2 top-10 translate-x-1/2 w-16 h-16 rounded-full items-center justify-center z-10"
                style={{
                    background: "var(--gradient-accent-v2)",
                    boxShadow:
                        "0 0 36px hsl(260 90% 65% / 0.6), 0 0 80px hsl(190 90% 50% / 0.25)",
                }}
            >
                <span className="font-v2-label text-[12px] text-white">{num}</span>
            </div>

            {/* Card placement — alternates via col-start */}
            <div className={`${isEven ? "md:col-start-1" : "md:col-start-2"}`} dir="rtl">
                <FlagshipCard flagship={flagship} num={num} tilt={tilt} />
            </div>
        </motion.li>
    );
};

const FlagshipsTimeline = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const spineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);

    return (
        <section
            ref={containerRef}
            className="py-20 md:py-32 relative"
            id="flagships"
            dir="rtl"
        >
            <div className="container mx-auto px-6 relative">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <p className="font-v2-label text-xs text-primary/80 mb-4">
                        منتجاتنا الرائدة
                    </p>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        مشاريع{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            نفخر بها
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        منتجات حقيقية، آلاف الاستخدامات اليومية، تنبض في السوق اليوم.
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Base spine */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px bg-white/[0.06]"
                    />
                    {/* Progressive violet→cyan fill on scroll */}
                    <motion.div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px origin-top"
                        style={{
                            scaleY: spineScale,
                            background:
                                "linear-gradient(180deg, hsl(260 90% 65% / 0.9), hsl(190 90% 50% / 0.9), hsl(260 90% 65% / 0.4))",
                        }}
                    />

                    <ol className="space-y-14 md:space-y-20">
                        {FLAGSHIPS.map((f, i) => (
                            <FlagshipRow key={f.key} flagship={f} index={i} />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default FlagshipsTimeline;
